// useAsync is an adaptation of usePromise with a few tweaks:
//   * The first parameter must be an async function (without parameters).
//   * If the async function is executed repeatedly (due to changing
//     dependencies) and the promises finish out of order, only the most
//     recently started execution is reflected in the return values. In other
//     words, starting a new execution effectively cancels any in flight.
//   * A fourth return value is added: a function for manually triggering a new
//     execution of the async function. For example, if the function retrieves
//     data from a server, and the data now needs to be refreshed, this refresh
//     function can be called.
// Example:
//   const [result, error, status, refresh] =
//     useAsync(async () => fetchUser(userId), [userId]);
//
// Adapted from https://github.com/bsonntag/react-use-promise
// With further inspiration from https://github.com/slorber/react-async-hook

import { type DependencyList, useCallback, useEffect, useRef, useState } from 'react';

type Restart = () => void;

type PendingState = [undefined, undefined, 'pending', Restart];
type ResolvedState<Result> = [Result, undefined, 'resolved', Restart];
type RejectedState = [undefined, Error, 'rejected', Restart];
type State<Result> = PendingState | ResolvedState<Result> | RejectedState;

function useCancellation() {
  const cancellationRef = useRef({ canceled: false });
  const cancel = () => {
    cancellationRef.current.canceled = true;
  };
  const reset = () => {
    cancel();
    const cancellation = { canceled: false };
    cancellationRef.current = cancellation;
    const isCanceled = () => cancellation.canceled;
    return [isCanceled] as const;
  };
  return [reset, cancel] as const;
}
/**
 * @deprecated
 * Use `useQuery` based hooks from `@tanstack/react-query` instead.
 */
export function useDeprecatedAsync<Result>(
  fn: () => Promise<Result>,
  deps: DependencyList
): State<Result> {
  const [state, setState] = useState<
    | [undefined, undefined, 'pending']
    | [Result, undefined, 'resolved']
    | [undefined, Error, 'rejected']
  >([undefined, undefined, 'pending']);
  const [reset, cancel] = useCancellation();
  const execute = useCallback(
    () => {
      const [isCanceled] = reset();
      fn().then(
        (result) => !isCanceled() && setState([result, undefined, 'resolved']),
        (error) => !isCanceled() && setState([undefined, error, 'rejected'])
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps
  );
  useEffect(
    () => {
      execute();
      return cancel;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps
  );
  return [...state, execute];
}
